import type {
    ControlProps, CSSObjectWithLabel, OptionProps, SingleValueProps, MenuProps,
} from 'react-select';

export const selectBaseStyles = {
    control<T, IsMulti extends boolean>(base: CSSObjectWithLabel, props: ControlProps<T, IsMulti>): CSSObjectWithLabel {
        return {
            ...base,
            // Remove conflicting styles - let CSS modules handle the styling
            border: 'none',
            borderRadius: 0,
            height: 'auto',
            minHeight: 'auto',
            fontSize: 'inherit',
            transition: 'none',
            background: 'transparent',
            cursor: props.isDisabled ? 'not-allowed' : 'pointer',
            boxShadow: 'none',
            '&:hover': {
                boxShadow: 'none',
                borderColor: 'transparent',
            },
        };
    },
    
    menu(base: CSSObjectWithLabel, props: MenuProps<any, any>): CSSObjectWithLabel {
        return {
            ...base,
            // Ensure proper positioning and z-index
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 9999,
            background: '#ffffff',
            margin: 0,
            overflow: 'visible',
            border: '1px solid var(--marriott-light-grey-2, #e4e7ec)',
            borderRadius: '12px',
            boxShadow: `
                0 25px 50px -12px rgba(0, 0, 0, 0.25),
                0 10px 20px -5px rgba(0, 0, 0, 0.1),
                0 0 0 1px rgba(255, 255, 255, 0.05),
                0 0 0 1px rgba(0, 0, 0, 0.05) inset
            `,
        };
    },
    
    option<T, IsMulti extends boolean>(base: CSSObjectWithLabel, props: OptionProps<T, IsMulti>): CSSObjectWithLabel {
        // Determine background color based on state
        let backgroundColor = 'transparent';
        let color = 'var(--marriott-dark-grey, #353944)';
        let fontWeight = '500';

        if (props.isSelected) {
            backgroundColor = 'var(--marriott-primary, #00a4ba)';
            color = 'white';
            fontWeight = '600';
        } else if (props.isFocused) {
            backgroundColor = 'var(--marriott-light-grey-3, #f5f6f8)';
            color = 'var(--marriott-primary, #00a4ba)';
        }

        // Determine hover colors
        let hoverBackgroundColor = 'var(--marriott-light-grey-3, #f5f6f8)';
        let hoverColor = 'var(--marriott-primary, #00a4ba)';
        
        if (props.isSelected) {
            hoverBackgroundColor = 'var(--marriott-primary, #00a4ba)';
            hoverColor = 'white';
        }

        // Determine active colors
        let activeBackgroundColor = 'var(--marriott-light-grey-2, #e4e7ec)';
        
        if (props.isSelected) {
            activeBackgroundColor = 'var(--marriott-dark-blue, #31697a)';
        }

        return {
            ...base,
            // Ensure options are visible by default but let CSS modules style them
            backgroundColor,
            color,
            padding: '12px 16px',
            fontSize: '14px',
            fontWeight,
            cursor: props.isDisabled ? 'not-allowed' : 'pointer',
            borderRadius: '8px',
            margin: '2px 0',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            ':hover': {
                backgroundColor: hoverBackgroundColor,
                color: hoverColor,
                transform: 'translateX(2px)',
            },
            ':active': {
                backgroundColor: activeBackgroundColor,
            },
        };
    },
    
    placeholder(base: CSSObjectWithLabel): CSSObjectWithLabel {
        return {
            ...base,
            color: 'var(--marriott-light-grey, #a7b5be)',
            fontWeight: '400',
            fontSize: '14px',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            margin: 0,
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
        };
    },
    
    valueContainer(base: CSSObjectWithLabel): CSSObjectWithLabel {
        return {
            ...base,
            padding: 0,
            margin: 0,
            flex: 1,
            position: 'relative',
        };
    },
    
    singleValue<T, IsMulti extends boolean>(
        base: CSSObjectWithLabel,
        props: SingleValueProps<T, IsMulti>,
    ): CSSObjectWithLabel {
        return {
            ...base,
            color: props.isDisabled 
                ? 'var(--marriott-light-grey, #a7b5be)' 
                : 'var(--marriott-dark-grey, #353944)',
            fontWeight: '500',
            fontSize: '14px',
            margin: 0,
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            maxWidth: 'calc(100% - 32px)', // Account for dropdown indicator
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        };
    },
    
    input(base: CSSObjectWithLabel): CSSObjectWithLabel {
        return {
            ...base,
            color: 'var(--marriott-dark-grey, #353944)',
            fontSize: '14px',
            fontWeight: '500',
            margin: 0,
            padding: 0,
        };
    },
    
    indicatorsContainer(base: CSSObjectWithLabel): CSSObjectWithLabel {
        return {
            ...base,
            padding: 0,
            margin: 0,
        };
    },
    
    indicatorSeparator(): CSSObjectWithLabel {
        return {
            display: 'none',
        };
    },
    
    clearIndicator(base: CSSObjectWithLabel): CSSObjectWithLabel {
        return {
            ...base,
            padding: 0,
            margin: 0,
            cursor: 'pointer',
        };
    },
    
    dropdownIndicator(base: CSSObjectWithLabel): CSSObjectWithLabel {
        return {
            ...base,
            padding: 0,
            margin: 0,
            cursor: 'pointer',
        };
    },
    
    menuList(base: CSSObjectWithLabel): CSSObjectWithLabel {
        return {
            ...base,
            padding: '8px',
            margin: 0,
            maxHeight: '200px',
            overflowY: 'auto',
        };
    },
    
    noOptionsMessage(base: CSSObjectWithLabel): CSSObjectWithLabel {
        return {
            ...base,
            color: 'var(--marriott-light-grey, #a7b5be)',
            fontSize: '14px',
            fontStyle: 'italic',
            padding: '12px 16px',
        };
    },
    
    loadingMessage(base: CSSObjectWithLabel): CSSObjectWithLabel {
        return {
            ...base,
            color: 'var(--marriott-light-grey, #a7b5be)',
            fontSize: '14px',
            padding: '12px 16px',
        };
    },
    
    menuPortal(base: CSSObjectWithLabel): CSSObjectWithLabel {
        return {
            ...base,
            zIndex: 99999,
        };
    },
};
